import type { HookAPI } from 'antd/es/modal/useModal'

import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'

import cn from './slottedImpactPreview.module.scss'

/**
 * Ответ dry-run эндпоинтов `.../preview` (ТЗ п.3.4).
 *
 * Бэкенд считает, сколько товаров пострадает при смене категории товара или
 * при изменении лимита присоединений категории: `truncatedProductsCount` —
 * у скольких обрежутся лишние значения, `expandedProductsCount` — у скольких
 * просто добавятся пустые слоты. `message` уже локализован.
 */
export type ImpactPreviewResponse = {
  hasImpact: boolean
  willTruncateData: boolean
  truncatedProductsCount: number
  expandedProductsCount: number
  message: string
}

/**
 * Дёргает `previewUrl` тем же телом, что уйдёт в основной запрос сохранения.
 *
 * Возвращает:
 *  - `null` — превью не смогло отработать (ошибка сети/сервера уже показана
 *    пользователю). Вызывающий код сам решает, продолжать ли; сейчас мы
 *    продолжаем, чтобы недоступность dry-run не блокировала сохранение.
 *  - `{ proceed: false }` — есть влияние, пользователь отменил сохранение.
 *  - `{ proceed: true }` — влияния нет либо пользователь подтвердил.
 */
export async function confirmSlottedImpact(params: {
  previewUrl: string
  body: unknown
  modal: HookAPI
  skipSanitize?: boolean
}): Promise<{ proceed: boolean } | null> {
  const { previewUrl, body, modal, skipSanitize } = params

  let preview: ImpactPreviewResponse
  try {
    const { data } = await api.post<ImpactPreviewResponse>(previewUrl, body, {
      _skipSanitize: skipSanitize,
    })
    preview = data
  } catch (error) {
    handleApiError(error, modal)
    return null
  }

  if (!preview?.hasImpact) return { proceed: true }

  const confirmed = await new Promise<boolean>((resolve) => {
    modal.confirm({
      title: preview.willTruncateData
        ? 'Часть значений будет удалена'
        : 'Подтвердите изменение',
      content: (
        <span
          className={`${cn.message} ${preview.willTruncateData ? cn.messageDanger : ''}`}
        >
          {preview.message}
        </span>
      ),
      okText: 'Продолжить',
      cancelText: 'Отмена',
      okButtonProps: preview.willTruncateData ? { danger: true } : undefined,
      centered: true,
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    })
  })

  return { proceed: confirmed }
}
